/*
 * Carbisdale - ContextView
 *
 * Rendering-Modul für den Context-Pane.
 * Zeigt Info-Text zeilen-synchron mit dem Editor.
 * Analog zu OutlineView, aber ohne Outline-Struktur.
 */

import { Style, EDITOR, LINE_HEIGHT_Q } from '../config.js'
import { parsedToFormatRuns, parsedCharsToString, charDisplayWidth } from '../TextProcessor.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { pickMarkerStyle, pickSeparatorMarkerStyle, MarkerPane } from './activeFieldHighlight.js'
import { enrichRunsWithSpellWarnings } from './spellWarningEnrich.js'

/**
 * ContextView-Klasse
 * Rendert Info-Text im Context-Pane.
 */
export class ContextView {
    /**
     * @param {EditorModel} model - Das Datenmodell
     * @param {EditorView} editorView - Die Editor-View (für Zeilen-Synchronisation)
     */
    constructor(model, editorView) {
        this.model = model
        this.editorView = editorView
    }

    /**
     * Liefert den passenden Marker-Style fuer den Context-Pane:
     * EDITOR_MARKER fuer den aktiven Absatz im info-Feld, sonst EDITOR_MARKER_DIM.
     * @param {*} paragraphId
     * @returns {string}
     */
    _markerStyle(paragraphId) {
        return pickMarkerStyle(MarkerPane.CONTEXT, paragraphId, this.model.cursor)
    }

    /**
     * Berechnet den Baseline-Shift in Pixeln für Info-Marker und Info-Text.
     * Info-Inhalt wird in 4QL (SECONDARY, System-Font) gerendert — bei größeren
     * Hauptzeilen muss er per Baseline verschoben werden (gleiche Formel wie
     * Absatzmarker in Main).
     * @param {number} mainHeightQ - heightQ der zugehörigen Hauptzeile
     * @returns {number} Pixel-Shift
     */
    _baselineShiftPx(mainHeightQ) {
        const renderer = this.editorView.renderer
        if (!renderer || !renderer.isProportional()) return 0
        return renderer.secondaryBaselineShiftPx(mainHeightQ)
    }

    /**
     * Rendert den sichtbaren Context-Bereich.
     * Zeilen-synchron mit dem Editor (gleicher viewportStart).
     */
    render(offsetX, offsetY, width, height, setCharFn) {
        if (this.editorView._useQuarterSpacing() && this.editorView.renderer) {
            this._renderLinesQuarterSpacing(offsetX, offsetY, width, height, setCharFn)
        } else {
            this._renderLines(offsetX, offsetY, width, height, 0, height, setCharFn)
        }
    }

    /**
     * Rendert nur die neu sichtbar gewordenen Zeilen nach einem Scroll-Shift.
     */
    renderPartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels = 0) {
        // Sub-Pixel-only-Frame (Proportional): nichts zu rendern, Canvas bleibt unverändert.
        // State wird zentral in EditorView.renderPartial gepflegt.
        if (this.editorView.renderer && shiftPixels === 0 && this.editorView._useQuarterSpacing()) {
            return
        }
        if (this.editorView.renderer && shiftPixels !== 0) {
            // Pixel-basiert — Proportional UND Monospace (beide mit Sub-Pixel-Scroll)
            const ev = this.editorView
            const useQuarter = ev._useQuarterSpacing() && ev._lineQuarterY.length > 0
            const ch = ev.renderer.charHeight
            const viewportPx = height * ch
            const absShift = Math.abs(shiftPixels)
            const exposedStart = shiftPixels > 0 ? viewportPx - absShift : 0
            const exposedEnd = shiftPixels > 0 ? viewportPx : absShift

            // Hintergrund der zu rendernden Zeilen clearen (Straddling-Artefakte)
            const panePixelX = offsetX * ev.renderer.charWidth
            const panePixelY = offsetY * ch
            const panePixelW = width * ev.renderer.charWidth

            let from = -1, to = -1
            for (let i = ev._firstVisibleLine('info'); i < ev.lines.length; i++) {
                const pxY = ev._lineScreenPixelY(i, 'info')
                if (pxY >= viewportPx) break
                const lineH = useQuarter ? (ev._paneCellHeightQ('info', i) * (ch / 4)) : ch
                if (pxY + lineH > exposedStart && pxY < exposedEnd) {
                    // Zeilen-Hintergrund clearen (gleiche Logik wie EditorView)
                    const clearTop = Math.max(0, pxY)
                    const clearBottom = Math.min(viewportPx, pxY + lineH)
                    ev.renderer.ctx.fillStyle = ev.renderer.colors.background
                    ev.renderer.ctx.fillRect(
                        panePixelX, panePixelY + clearTop,
                        panePixelW, clearBottom - clearTop
                    )
                    if (from < 0) from = i
                    to = i + 1
                }
            }
            if (from >= 0) {
                this._renderLinesForRange(offsetX, offsetY, width, height, from, to, setCharFn)
            }
        } else if (scrollDelta > 0) {
            const startY = height - scrollDelta
            this._renderLines(offsetX, offsetY, width, height, startY, height, setCharFn)
        } else {
            this._renderLines(offsetX, offsetY, width, height, 0, Math.abs(scrollDelta), setCharFn)
        }
    }

    /**
     * Rendert nur die dirty Zeilen-Ranges (Paragraph-Partial-Pfad).
     */
    renderDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn) {
        for (const range of dirtyRanges) {
            if (this.editorView._useQuarterSpacing()) {
                for (let li = range.fromLineIndex; li < range.toLineIndex; li++) {
                    const screenY = this.editorView._lineToScreenY(li, 'info')
                    const heightQ = this.editorView._paneCellHeightQ('info', li)
                    clearLineFn(offsetX, offsetY + screenY, width, heightQ)
                }
                this._renderLinesForRange(offsetX, offsetY, width, height,
                    range.fromLineIndex, range.toLineIndex, setCharFn)
            } else {
                const fromY = range.fromLineIndex - this.editorView.viewportStart
                const toY = range.toLineIndex - this.editorView.viewportStart
                for (let y = fromY; y < toY; y++) {
                    clearLineFn(offsetX, offsetY + y, width)
                }
                this._renderLines(offsetX, offsetY, width, height, fromY, toY, setCharFn)
            }
        }
    }

    /**
     * Rendert alle sichtbaren Info-Zeilen mit Quarter-Spacing (Proportional-Mode).
     */
    _renderLinesQuarterSpacing(offsetX, offsetY, width, height, setCharFn) {
        const ev = this.editorView
        const renderer = ev.renderer
        const viewportEndPx = height * renderer.charHeight
        const infoLines = ev.infoLines
        const infoTextWidth = ev.getInfoTextWidth()

        if (infoTextWidth <= 0) return

        const quarterH = renderer.charHeight / 4
        for (let i = ev._firstVisibleLine('info'); i < infoLines.length; i++) {
            const pxY = ev._lineScreenPixelY(i, 'info')
            if (pxY >= viewportEndPx) break
            const lineH = ev._paneCellHeightQ('info', i) * quarterH
            if (pxY + lineH <= 0) continue

            const fractionalY = pxY / renderer.charHeight
            const infoLine = infoLines[i]

            if (infoLine.isSeparator) {
                this._renderSeparatorLine(offsetX, offsetY + fractionalY, width, infoLine, setCharFn)
                continue
            }

            this._renderInfoLine(
                offsetX, offsetY + fractionalY, width,
                infoTextWidth, infoLine, i, setCharFn, ev.lines[i]?.heightQ || 4
            )
        }
    }

    /**
     * Rendert Info-Zeilen für einen Bereich von lineIndices (dirty-Range-Rendering).
     */
    _renderLinesForRange(offsetX, offsetY, width, height, fromLineIndex, toLineIndex, setCharFn) {
        const ev = this.editorView
        const infoLines = ev.infoLines
        const infoTextWidth = ev.getInfoTextWidth()

        if (infoTextWidth <= 0) return

        for (let i = fromLineIndex; i < toLineIndex; i++) {
            if (i >= infoLines.length) break
            const fractionalY = ev._lineToScreenY(i, 'info')
            const infoLine = infoLines[i]

            if (infoLine.isSeparator) {
                this._renderSeparatorLine(offsetX, offsetY + fractionalY, width, infoLine, setCharFn)
                continue
            }

            const mainLine = ev.lines[i]
            this._renderInfoLine(
                offsetX, offsetY + fractionalY, width,
                infoTextWidth, infoLine, i, setCharFn, mainLine?.heightQ || 4
            )
        }
    }

    /**
     * Rendert eine Separator-Zeile im Context-Pane.
     * Erst durchgehende ─ Linie, dann Label darüber (nur wenn Context einziges Pane).
     */
    _renderSeparatorLine(offsetX, y, width, infoLine, setCharFn) {
        const isBackgroundSep = infoLine.separatorDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH
        const bgSuffix = infoLine.isBackground ? '+BG' : ''
        const lineChar = isBackgroundSep ? '═' : '─'
        // Linie + Icon-Borders identisch zu Main/Outline: passiv Connector-Farbe,
        // aktiv (Cursor auf diesem Separator) Active-Border-Color.
        const isActiveSep = this.editorView.model.cursor
            && this.editorView.model.cursor.paragraphId === infoLine.paragraphId
        const hrStyle = isActiveSep ? Style.OUTLINE_CURRENT : Style.OUTLINE_CONNECTOR

        // 1. Durchgehende Linie
        for (let i = 0; i < width; i++) {
            setCharFn(offsetX + i, y, lineChar, hrStyle)
        }

        const outlineHidden = this.editorView.outlinePaneWidth <= 0
        const mainHidden = this.editorView.mainPaneWidth <= 0

        // Lock-Control rechts (v)/( ) wenn Main hidden — analog zum Main-Separator
        // (Position width - 4, gleich wie EditorView._renderSeparatorLine).
        const showLockHere = mainHidden && !isBackgroundSep && width >= 8
        if (showLockHere) {
            const renderer = this.editorView.renderer
            const isProp = renderer && renderer.isProportional()
            const locked = this.editorView.model.isSectionLocked(infoLine.paragraphId)
            const lockChar = locked ? 'v' : ' '
            const controlX = width - 4
            if (renderer) {
                const cw = renderer.charWidth
                const ch = renderer.charHeight
                renderer.ctx.fillStyle = renderer.colors.background
                renderer.ctx.fillRect((offsetX + controlX) * cw, y * ch, 3 * cw, ch)
            }
            if (isProp) {
                renderer._renderControlIcon(offsetX + controlX + 1, y, lockChar, Style.CONTROL_NORMAL, hrStyle)
            } else {
                setCharFn(offsetX + controlX, y, '(', hrStyle)
                setCharFn(offsetX + controlX + 1, y, lockChar, Style.CONTROL_NORMAL)
                setCharFn(offsetX + controlX + 2, y, ')', hrStyle)
            }
        }

        // 2. Label nur wenn Context das einzig sichtbare Pane
        if (!outlineHidden || !mainHidden) return

        let label
        if (isBackgroundSep) {
            const docTitle = this.editorView.model.metadata && this.editorView.model.metadata.title
            label = docTitle ? `Background · ${docTitle}` : 'Background'
        } else {
            label = infoLine.separatorTitle || infoLine.separatorNumber || ''
        }
        // Background-Sep ohne Label: nichts zu rendern. Editierbar ohne Label:
        // Marker trotzdem rendern (>< direkt aneinander) — kein early-return.
        if (isBackgroundSep && !label) return
        if (!isBackgroundSep && width < 8) return

        const title = infoLine.separatorTitle || ''
        // Editierbarkeit haengt vom Typ ab, nicht vom Inhalt — sonst waeren
        // Marker bei leerem Label dim, obwohl der Cursor hier steht.
        const isEditableTitle = !isBackgroundSep
        const isTitle = (label === title && title !== '') || isBackgroundSep
        // Titel bold in Textfarbe (SEPARATOR_TITLE), Nummer-Fallback in Textfarbe
        // nicht-bold (EDITOR_TEXT statt früher EDITOR_HR).
        const labelStyle = (isTitle ? Style.SEPARATOR_TITLE : Style.EDITOR_TEXT) + bgSuffix
        const labelCol = 2  // 2 Zeichen Abstand vom Rand

        // Separator-Input-State nur verwenden wenn AKTIV in DIESEM Pane UND
        // fuer DIESE Separator-Zeile editiert wird. Sonst wuerde der scrollOffset
        // des gerade editierten (langen) Labels alle anderen Labels kuerzen.
        const rawSepState = isEditableTitle && this.editorView._separatorInputAccessor
            ? this.editorView._separatorInputAccessor() : null
        const isActiveSeparatorInput = rawSepState
            && rawSepState.pane === 'context'
            && this.editorView.model.cursor
            && this.editorView.model.cursor.paragraphId === infoLine.paragraphId
        const sepState = isActiveSeparatorInput ? rawSepState : null

        // Selection-Info
        const hasSel = sepState && sepState.selectionStart !== sepState.selectionEnd
            && sepState.text === title

        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()

        // Marker-Style: voll wenn Cursor hier UND Edit-Pane === context, sonst dim.
        // Background bekommt keine Marker (nicht editierbar).
        const markerStyle = isBackgroundSep
            ? null
            : pickSeparatorMarkerStyle(MarkerPane.CONTEXT, infoLine.paragraphId,
                this.editorView.model.cursor, sepState)

        // Hintergrund-Farbe fuer Overlays — analog _separatorOverlayBg im Main:
        // im Context-Pane gibt's keinen Active-Row-Background, also immer default.
        const overlayBg = renderer && renderer.colors ? renderer.colors.background : null

        // scrollOffset anwenden wenn Separator hier editiert wird
        const scrollOffset = sepState ? sepState.scrollOffset : 0
        const displayLabel = label ? label.slice(scrollOffset) : ''

        if (isProp) {
            const charWidth = renderer.charWidth
            const charHeight = renderer.charHeight
            const pixelY = y * charHeight
            const isEmpty = displayLabel.length === 0 && !isBackgroundSep

            let selRange = null
            if (hasSel) {
                const sMin = Math.min(sepState.selectionStart, sepState.selectionEnd)
                const sMax = Math.max(sepState.selectionStart, sepState.selectionEnd)
                selRange = {
                    start: Math.max(0, sMin - scrollOffset),
                    end: Math.max(0, sMax - scrollOffset)
                }
            }

            const formatRuns = [{
                start: 0, end: displayLabel.length,
                bold: isTitle, italic: false, underline: false, strikethrough: false,
                style: labelStyle
            }]

            const labelStartPx = (offsetX + labelCol) * charWidth  // direkt am labelCol
            const paneEndPx = (offsetX + width) * charWidth

            // Label-Breite berechnen
            const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
            const layout = computeStringLayout(displayLabel, activeCache, activeCache.baseStep, formatRuns)
            const labelWidthPx = layout.totalWidth * activeSF
            // Marker passen sich der Linien-Hoehe an (kleiner auf Separator).
            // Glyph-Box ebenfalls proportional, sonst Luecke zwischen Glyph
            // und Label (Glyph wird linksbuendig in Box gemalt).
            const lineHQ = infoLine.heightQ || 4
            const markerHQ = lineHQ
            const markerWidthPx = renderer.markerWidthPx() * lineHQ / LINE_HEIGHT_Q.NORMAL

            // BG-Block ueber > Label < damit die ─ Linie nicht durch die Marker
            // schimmert. Bei isEmpty: nur >< Marker. Ohne Marker (Background-
            // Sep im Context): charWidth Spacer statt Marker.
            if (markerStyle || !isEmpty) {
                const sideWidth = markerStyle ? markerWidthPx : charWidth
                const startPx = labelStartPx - sideWidth
                const totalPx = sideWidth + labelWidthPx + sideWidth
                renderer.ctx.fillStyle = overlayBg
                renderer.ctx.fillRect(startPx, pixelY, totalPx, charHeight)
            }

            // Marker > / < links/rechts (nur fuer editierbare Separatoren).
            // markerHQ === lineHQ → Glyph-Mitte = pixelY + charHeight/2.
            // Im Context-Pane sitzen Marker mit pixelY noch eine Quarterline
            // zu tief (Boris-Test) — daher -charHeight/4 anhaengen.
            if (markerStyle) {
                const markerVShift = -charHeight / 4
                const markerPixelY = pixelY + markerVShift
                const startMarkerPx = labelStartPx - markerWidthPx
                const endMarkerPx = labelStartPx + labelWidthPx
                const markerRuns = [{
                    start: 0, end: 1, bold: false, italic: false,
                    underline: false, strikethrough: false, style: markerStyle
                }]
                renderer.renderProportionalLine(
                    renderer.ctx, '>', startMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
                renderer.renderProportionalLine(
                    renderer.ctx, '<', endMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
            }

            if (displayLabel.length > 0) {
                renderer.renderProportionalLine(
                    renderer.ctx, displayLabel, labelStartPx, pixelY,
                    formatRuns, selRange, null, null
                )
            }
        } else {
            // Monospace: > Label < direkt (kein Spacer)
            const markerReserve = markerStyle ? 1 : 0
            const maxLen = width - labelCol - 1 - 2 * markerReserve
            if (maxLen <= 0 && !markerStyle) return
            const display = (label && maxLen > 0)
                ? displayLabel.slice(0, maxLen)
                : ''
            const isEmpty = display.length === 0 && !isBackgroundSep

            // Selection-Grenzen berechnen
            let selStart = -1, selEnd = -1
            if (hasSel) {
                const sMin = Math.min(sepState.selectionStart, sepState.selectionEnd)
                const sMax = Math.max(sepState.selectionStart, sepState.selectionEnd)
                selStart = Math.max(0, Math.min(sMin - scrollOffset, display.length))
                selEnd = Math.max(0, Math.min(sMax - scrollOffset, display.length))
            }

            // BG-Block ueber > Label < damit die ─ Linie nicht durch die Marker
            // schimmert. Background-Sep ohne Marker: nur unter dem Label.
            if (renderer && (markerStyle || !isEmpty)) {
                const charWidth = renderer.charWidth
                const charHeight = renderer.charHeight
                const startCol = markerStyle ? labelCol - 1 : labelCol
                const totalCols = (markerStyle ? 1 : 0)
                    + (isEmpty ? 0 : display.length)
                    + (markerStyle ? 1 : 0)
                renderer.ctx.fillStyle = overlayBg
                renderer.ctx.fillRect((offsetX + startCol) * charWidth, y * charHeight,
                    totalCols * charWidth, charHeight)
            }

            if (isEmpty) {
                // ><  direkt aneinander
                if (markerStyle) {
                    setCharFn(offsetX + labelCol - 1, y, '>', markerStyle)
                    setCharFn(offsetX + labelCol, y, '<', markerStyle)
                }
            } else {
                if (markerStyle) {
                    setCharFn(offsetX + labelCol - 1, y, '>', markerStyle)
                }

                let col = labelCol
                for (let i = 0; i < display.length && col < width - 1; i++) {
                    const selected = hasSel && i >= selStart && i < selEnd
                    setCharFn(offsetX + col++, y, display[i], labelStyle, selected)
                }

                if (markerStyle && col < width) {
                    setCharFn(offsetX + col, y, '<', markerStyle)
                }
            }
        }

    }

    /**
     * Rendert einen Bereich von Info-Zeilen (Monospace-Pfad).
     */
    _renderLines(offsetX, offsetY, width, height, fromY, toY, setCharFn) {
        const viewportStart = this.editorView.viewportStart
        const infoLines = this.editorView.infoLines
        const infoTextWidth = this.editorView.getInfoTextWidth()

        if (infoTextWidth <= 0) return

        for (let y = fromY; y < toY; y++) {
            const lineIndex = viewportStart + y

            if (lineIndex >= infoLines.length) break

            // Separator-Zeile: Titel oder Fallback-Nummer
            const infoLine = infoLines[lineIndex]
            if (infoLine.isSeparator) {
                this._renderSeparatorLine(offsetX, offsetY + y, width, infoLine, setCharFn)
                continue
            }

            this._renderInfoLine(
                offsetX, offsetY + y, width,
                infoTextWidth,
                infoLine, lineIndex,
                setCharFn, 4
            )
        }
    }

    /**
     * Rendert eine einzelne Info-Zeile mit >< Markern.
     */
    _renderInfoLine(offsetX, y, paneWidth, infoTextWidth, infoLine, infoLineIndex, setCharFn, mainHeightQ = 4) {
        if (infoLine.isPadding) return

        const para = this.model.getParagraph(infoLine.paragraphId)
        if (!para) return

        const bgSuffix = infoLine.isBackground ? '+BG' : ''

        // Proportional: Marker als 4QL-Glyphen rendern (System-Font, wie Info-Text)
        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()
        const MARKER_HQ = LINE_HEIGHT_Q.SECONDARY

        // Baseline-Alignment: Info an Hauptzeile ausrichten (wie Absatzmarker in Main)
        const baselineShift = this._baselineShiftPx(mainHeightQ)

        // Leere Info: >< nur wenn Cursor hier
        if (!para.info) {
            const cursor = this.model.cursor
            const cursorHere = cursor && cursor.field === 'info' && cursor.paragraphId === para.id
            if (cursorHere && infoLine.isFirstLine) {
                if (isProp) {
                    const mPx = offsetX * renderer.charWidth
                    const mPy = y * renderer.charHeight + baselineShift
                    renderer.renderProportionalLine(
                        renderer.ctx, '>', mPx, mPy,
                        [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                        null, null, null, MARKER_HQ
                    )
                    if (1 < paneWidth) {
                        const ePx = mPx + renderer.markerWidthPx(MARKER_HQ)
                        renderer.renderProportionalLine(
                            renderer.ctx, '<', ePx, mPy,
                            [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                            null, null, null, MARKER_HQ
                        )
                    }
                } else {
                    if (0 < paneWidth) setCharFn(offsetX, y, '>', this._markerStyle(para.id))
                    if (1 < paneWidth) setCharFn(offsetX + 1, y, '<', this._markerStyle(para.id))
                }
            }
            return
        }

        // > Marker (nur bei erster Zeile)
        if (0 >= paneWidth) return

        if (infoLine.isFirstLine) {
            if (isProp) {
                const mPx = offsetX * renderer.charWidth
                const mPy = y * renderer.charHeight + baselineShift
                renderer.renderProportionalLine(
                    renderer.ctx, '>', mPx, mPy,
                    [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                    null, null, null, MARKER_HQ
                )
            } else {
                setCharFn(offsetX, y, '>', this._markerStyle(para.id))
            }
        } else {
            setCharFn(offsetX, y, ' ', Style.EDITOR_TEXT + bgSuffix)
        }

        // Per-Paragraph Info-Parsing (gecacht, Display-Indizes mit dataOffset)
        const fullParsed = this.editorView._getParsedRichText(this.editorView._infoParseCache, para.id, para.info, 'normal', 'info')
        const parsed = this.editorView._sliceParsedForLine(fullParsed, infoLine.startOffset, infoLine.endOffset)
        const highlightFn = this.editorView.searchHighlightFn

        // Proportional: pixelgenau rendern
        if (renderer && renderer.isProportional() && parsed.length > 0) {
            this._renderInfoLineProportional(
                offsetX, y, paneWidth, infoTextWidth,
                infoLine, infoLineIndex, parsed, para, highlightFn, renderer, setCharFn,
                baselineShift
            )
            return
        }

        // Spellcheck-Wavy fuer Info (Mono-Pfad). Pendant zur Logik in
        // `EditorView._renderLineBody` und `OutlineView._renderNoteLine`.
        const infoWarnings = this.editorView._getEffectiveSpellWarnings(infoLine.paragraphId, 'info')
        const hasInfoWarnings = infoWarnings && infoWarnings.length > 0
        const spellRanges = []
        let curWavy = null

        let col = 0
        for (let i = 0; i < parsed.length && col < infoTextWidth; i++) {
            const charX = offsetX + 1 + col
            if (charX - offsetX >= paneWidth) break

            const { char, style, dataOffset } = parsed[i]

            // Selection-Highlighting (inkl. Link-Selektion)
            const isSelected = this.editorView._isPositionSelectedCached(infoLine.paragraphId, dataOffset, 'info')
                || this.editorView._isLinkCharSelected(infoLine.paragraphId, 'info', parsed[i])

            // Such-Highlighting
            const highlightType = highlightFn
                ? highlightFn(infoLine.paragraphId, dataOffset, 'info')
                : null
            setCharFn(charX, y, char, style + bgSuffix, isSelected, highlightType)
            const dispW = charDisplayWidth(char)

            if (hasInfoWarnings) {
                let inWarn = false
                for (let wi = 0; wi < infoWarnings.length; wi++) {
                    const w = infoWarnings[wi]
                    if (dataOffset >= w.start && dataOffset < w.start + w.length) {
                        inWarn = true
                        break
                    }
                }
                if (inWarn) {
                    if (curWavy && curWavy.gridX + curWavy.gridW === charX) {
                        curWavy.gridW += dispW
                    } else {
                        curWavy = { gridX: charX, gridW: dispW }
                        spellRanges.push(curWavy)
                    }
                } else {
                    curWavy = null
                }
            }

            col += dispW
        }

        // < Marker am Ende (nur bei letzter Zeile)
        if (infoLine.isLastLine) {
            const endX = offsetX + 1 + col
            if (endX - offsetX < paneWidth) {
                setCharFn(endX, y, '<', this._markerStyle(para.id))
            }
        }

        // Wavy-Underlines NACH allen Glyphen — additiv. Dim, wenn der Cursor
        // nicht in diesem Absatz steht.
        if (spellRanges.length > 0 && renderer && renderer.drawSpellWavyMonospace) {
            const dim = !this.editorView.isLineActiveParagraph(infoLine)
            for (const r of spellRanges) {
                renderer.drawSpellWavyMonospace(r.gridX, y, r.gridW, dim)
            }
        }
    }

    /**
     * Rendert eine Info-Zeile im Proportional-Modus.
     */
    _renderInfoLineProportional(offsetX, y, paneWidth, infoTextWidth,
                                 infoLine, infoLineIndex, parsed, para, highlightFn,
                                 renderer, setCharFn, baselineShift = 0) {
        const lineText = parsedCharsToString(parsed)
        let formatRuns = parsedToFormatRuns(parsed)

        // Background-Suffix an formatRun-Styles anhängen
        const bgSuffix = infoLine.isBackground ? '+BG' : ''
        if (bgSuffix) {
            formatRuns = formatRuns.map(run => ({ ...run, style: run.style + bgSuffix }))
        }

        // Spellcheck-Wavy fuer Info (Proportional-Pfad). Pendant zur Logik
        // in `EditorView._renderLineProportional`.
        const localSpellWarnings = this.editorView.buildLocalSpellWarnings(infoLine, 'info')
        if (localSpellWarnings) {
            formatRuns = enrichRunsWithSpellWarnings(
                formatRuns, localSpellWarnings, null,
                !this.editorView.isLineActiveParagraph(infoLine)
            )
        }

        const charWidth = renderer.charWidth
        const charHeight = renderer.charHeight
        const infoHeightQ = renderer.isProportional() ? LINE_HEIGHT_Q.SECONDARY : 4
        // Proportional: > Marker in 4QL (System-Font) reserviert markerWidthPx(SECONDARY)
        const textStartX = renderer.isProportional()
            ? offsetX * charWidth + renderer.markerWidthPx(infoHeightQ)
            : (offsetX + 1) * charWidth
        const pixelY = y * charHeight + baselineShift

        // Selection-Range berechnen
        let selRange = null
        let selStart = -1, selEnd = -1
        for (let i = 0; i < parsed.length; i++) {
            const isSel = this.editorView._isPositionSelectedCached(infoLine.paragraphId, parsed[i].dataOffset, 'info')
                || this.editorView._isLinkCharSelected(infoLine.paragraphId, 'info', parsed[i])
            if (isSel && selStart < 0) selStart = i
            if (isSel) selEnd = i + 1
        }
        if (selStart >= 0) {
            selRange = { start: selStart, end: selEnd }
        }

        // Such-Highlighting VOR dem Text rendern
        if (highlightFn) {
            const posMap = infoLineIndex >= 0 ? this.editorView._getPositionMap(infoLineIndex, 'info') : null
            if (posMap) {
                // Y/Höhe analog zur Selection in renderProportionalLine berechnen,
                // sonst rutscht der Highlight visuell nach oben (siehe selVertOffset
                // in Renderer.renderProportionalLine).
                const selVertOffset = renderer._verticalPad * infoHeightQ / 4
                const selPixelY = pixelY + selVertOffset
                const selHeight = renderer.textHeightForHeightQ(infoHeightQ)
                this._renderInfoSearchHighlights(
                    renderer, parsed, posMap, infoLine.paragraphId,
                    textStartX, selPixelY, selHeight, highlightFn
                )
            }
        }

        // End-Marker im proportionalen Fluss
        let endMarker = null
        if (infoLine.isLastLine) {
            endMarker = {
                char: '<',
                style: this._markerStyle(para.id),
                selected: false,
                heightQ: infoHeightQ
            }
        }

        // Layout aus PositionMap wiederverwenden
        const infoLineMap = infoLineIndex >= 0 ? this.editorView._getPositionMap(infoLineIndex, 'info') : null
        const rawLayout = infoLineMap ? infoLineMap._rawLayout : null

        renderer.renderProportionalLine(
            renderer.ctx,
            lineText,
            textStartX,
            pixelY,
            formatRuns,
            selRange,
            endMarker,
            rawLayout,
            infoHeightQ
        )
    }

    /**
     * Rendert Such-Highlighting für eine proportionale Info-Zeile.
     * selPixelY/selHeight sind die vertikal zentrierten Werte (analog zur Selection),
     * nicht die nackte Zeilen-Top-Y plus volle charHeight.
     */
    _renderInfoSearchHighlights(renderer, parsed, posMap, paragraphId, textStartX, selPixelY, selHeight, highlightFn) {
        let hlStart = -1
        let hlType = null

        for (let i = 0; i <= parsed.length; i++) {
            const currentHl = i < parsed.length
                ? highlightFn(paragraphId, parsed[i].dataOffset, 'info')
                : null

            if (currentHl !== hlType) {
                if (hlType && hlStart >= 0) {
                    const startPx = textStartX + (hlStart < posMap.xPositions.length ? posMap.xPositions[hlStart] : posMap.totalWidth)
                    const endPx = textStartX + (i < posMap.xPositions.length ? posMap.xPositions[i] : posMap.totalWidth)

                    const bgColor = renderer._resolveSearchHighlightColor(hlType)
                    if (bgColor) {
                        renderer.ctx.fillStyle = bgColor
                        renderer.ctx.fillRect(startPx, selPixelY, endPx - startPx, selHeight)
                    }
                }
                hlStart = i
                hlType = currentHl
            }
        }
    }
}
